import React, { forwardRef } from 'react';
import {
  Badge,
  Button,
  Divider,
  Label,
  MenuToggle,
  MenuToggleElement,
  Skeleton,
  Tabs,
  Tooltip,
} from '@patternfly/react-core';
import SVG from 'react-inlinesvg';

import './styles.scss';
import classNames from 'classnames';

/** Use for addAlert({ className: LandingLayout.alertClassName }) */
export const LANDING_LAYOUT_ALERT_CLASS = 'overview__alert';

/**
 * Compound component for Course and Premium landing page layout.
 * Use subcomponents instead of overview__* class names for consistent styling.
 *
 * @example
 * <LandingLayout>
 *   <LandingLayout.Breadcrumb>
 *     <Link href routeName="catalog">Catalog</Link>
 *     <BreadcrumbItem isActive>{title}</BreadcrumbItem>
 *   </LandingLayout.Breadcrumb>
 *   <LandingLayout.Header title={title} subtitle={...} />
 *   <LandingLayout.Content>
 *     <LandingLayout.Main>
 *       <LandingLayout.Tabs activeKey={...} onSelect={...}>
 *         <Tab eventKey="overview" ... />
 *       </LandingLayout.Tabs>
 *     </LandingLayout.Main>
 *     <LandingLayout.Sidebar>{sidebar}</LandingLayout.Sidebar>
 *   </LandingLayout.Content>
 * </LandingLayout>
 */
const LandingLayoutRoot: React.FC<{
  children: React.ReactNode;
  className?: string;
}> = ({ children, className = '' }) => (
  <div className={`container-grid-responsive ${className}`}>
    <div className="overview">{children}</div>
  </div>
);

const LandingLayoutBreadcrumb: React.FC<{ children: React.ReactNode }> = ({
  children,
}) => <div className="overview__breadcrumb">{children}</div>;

const LandingLayoutHeader: React.FC<{
  title: string;
  subtitle?: React.ReactNode;
}> = ({ title, subtitle }) => (
  <div className="overview__header">
    {subtitle}
    <h1>{title}</h1>
    <Divider component="div" className="overview__divider" />
  </div>
);

const LandingLayoutContent: React.FC<{ children: React.ReactNode }> = ({
  children,
}) => <div className="overview__content">{children}</div>;

const LandingLayoutMain: React.FC<{
  children: React.ReactNode;
  className?: string;
}> = ({ children, className = '' }) => (
  <div className={`overview__main ${className}`.trim()}>{children}</div>
);

const LandingLayoutSidebar: React.FC<{ children: React.ReactNode }> = ({
  children,
}) => <div className="overview__sidebar">{children}</div>;

const LandingLayoutTabs: React.FC<React.ComponentProps<typeof Tabs>> = ({
  className,
  ...props
}) => (
  <Tabs {...props} className={`overview__tabs ${className ?? ''}`.trim()} />
);

const LandingLayoutSidebarButton = forwardRef<
  HTMLButtonElement,
  React.ComponentProps<typeof Button> & { isCancel?: boolean }
>(({ isCancel, className = '', ...props }, ref) => (
  <Button
    ref={ref}
    {...props}
    className={`overview__mobile-fluid-width ${
      isCancel ? 'overview__btn-cancel' : ''
    } ${className}`.trim()}
  />
));

LandingLayoutSidebarButton.displayName = 'LandingLayoutSidebarButton';

const LandingLayoutSidebarActions: React.FC<{ children: React.ReactNode }> = ({
  children,
}) => <div className="overview__actions">{children}</div>;

const LandingLayoutProgress: React.FC<{ children: React.ReactNode }> = ({
  children,
}) => <div className="overview__progress">{children}</div>;

const LandingLayoutEmptyMessage: React.FC<{ children: React.ReactNode }> = ({
  children,
}) => <p className="overview__empty-message">{children}</p>;

const LandingLayoutSidebarDropdownToggle = forwardRef<
  MenuToggleElement,
  React.ComponentProps<typeof MenuToggle>
>(({ className = '', ...props }, ref) => (
  <MenuToggle
    ref={ref}
    {...props}
    className={`overview__dropdown ${className}`.trim()}
  />
));

LandingLayoutSidebarDropdownToggle.displayName =
  'LandingLayoutSidebarDropdownToggle';

export type SidebarLinkVariant =
  | 'access-class-active'
  | 'access-class-disabled'
  | 'reschedule-active'
  | 'reschedule-disabled';

/** Use with Link: className={LandingLayout.sidebarLinkClassName('access-class-active')} */
const sidebarLinkClassName = (variant: SidebarLinkVariant): string =>
  ({
    'access-class-active': 'overview__access-class-active-link',
    'access-class-disabled': 'overview__access-class-disabled-link',
    'reschedule-active': 'overview__reschedule-active-link',
    'reschedule-disabled': 'overview__reschedule-disabled-link',
  }[variant]);

const LandingLayoutMetadata: React.FC<{ children: React.ReactNode }> = ({
  children,
}) => <div className="overview__metadata">{children}</div>;

const LandingLayoutMetadataWithIcon: React.FC<{
  children: React.ReactNode;
  iconSrc?: string;
  isSkeleton?: boolean;
}> = ({ children, iconSrc, isSkeleton = false }) => (
  <div className="overview__metadata-with-icon">
    {isSkeleton && <Skeleton height="24px" width="24px" />}
    {iconSrc && <SVG src={iconSrc} height={24} width={24} />}
    {children}
  </div>
);

const LandingLayoutMetadataText: React.FC<{ children: React.ReactNode }> = ({
  children,
}) => <p className="overview__metadata-text">{children}</p>;

const METADATA_LABEL_COLOR = {
  green: 'green',
  gray: 'grey',
  yellow: 'gold',
} as const;

const LandingLayoutMetadataChips: React.FC<{
  chips: {
    badgeValue: string | number;
    children: React.ReactNode;
    color?: 'green' | 'gray' | 'yellow';
    dataAnalyticsId?: string;
    tooltipContent?: string;
  }[];
}> = ({ chips }) => (
  <div className="overview__metadata-chip-container">
    {chips.map(
      ({ badgeValue, children, color, dataAnalyticsId, tooltipContent }) => {
        const key = `${String(children)}-${badgeValue}`;
        const label = (
          <Label
            className={classNames('overview__metadata-chip', color)}
            color={color ? METADATA_LABEL_COLOR[color] : undefined}
            data-analytics-id={dataAnalyticsId}
            variant="filled"
          >
            {children}
            <Badge isRead>{badgeValue}</Badge>
          </Label>
        );

        return tooltipContent ? (
          <Tooltip key={key} content={tooltipContent}>
            {label}
          </Tooltip>
        ) : (
          <React.Fragment key={key}>{label}</React.Fragment>
        );
      },
    )}
  </div>
);

const LandingLayoutContentHeading: React.FC<{ children: React.ReactNode }> = ({
  children,
}) => <h3 className="overview__content-heading">{children}</h3>;

const LandingLayoutContentText: React.FC<
  React.ComponentProps<'p'> & { children: React.ReactNode }
> = ({ children, ...props }) => (
  <p className="overview__content-text" {...props}>
    {children}
  </p>
);

const LandingLayoutList: React.FC<
  React.ComponentProps<'ul'> & { children: React.ReactNode }
> = ({ children, ...props }) => (
  <ul className="overview__list" {...props}>
    {children}
  </ul>
);

const LandingLayoutCommunity: React.FC<{ children: React.ReactNode }> = ({
  children,
}) => <div className="overview__community">{children}</div>;

const LandingLayoutExploreButton: React.FC<
  React.ComponentProps<typeof Button>
> = ({ className, ...props }) => (
  <Button
    {...props}
    className={`overview__explore-button ${className ?? ''}`.trim()}
  />
);

const LandingLayoutLiveClassSchedulingInfo: React.FC<{
  children: React.ReactNode;
}> = ({ children }) => (
  <div className="overview__live-class-scheduling-info">{children}</div>
);

const LandingLayoutImportantNotes: React.FC<
  React.ComponentProps<'tr'> & { children: React.ReactNode }
> = ({ children, ...props }) => (
  <tr className="overview__important_notes" {...props}>
    {children}
  </tr>
);

export const LandingLayout = Object.assign(LandingLayoutRoot, {
  alertClassName: LANDING_LAYOUT_ALERT_CLASS,
  Breadcrumb: LandingLayoutBreadcrumb,
  Header: LandingLayoutHeader,
  Content: LandingLayoutContent,
  Main: LandingLayoutMain,
  Sidebar: LandingLayoutSidebar,
  Tabs: LandingLayoutTabs,
  SidebarButton: LandingLayoutSidebarButton,
  SidebarActions: LandingLayoutSidebarActions,
  Progress: LandingLayoutProgress,
  EmptyMessage: LandingLayoutEmptyMessage,
  SidebarDropdownToggle: LandingLayoutSidebarDropdownToggle,
  sidebarLinkClassName,
  Metadata: LandingLayoutMetadata,
  MetadataWithIcon: LandingLayoutMetadataWithIcon,
  MetadataText: LandingLayoutMetadataText,
  MetadataChips: LandingLayoutMetadataChips,
  ContentHeading: LandingLayoutContentHeading,
  ContentText: LandingLayoutContentText,
  List: LandingLayoutList,
  Community: LandingLayoutCommunity,
  ExploreButton: LandingLayoutExploreButton,
  LiveClassSchedulingInfo: LandingLayoutLiveClassSchedulingInfo,
  ImportantNotes: LandingLayoutImportantNotes,
});

export default LandingLayout;
